/* Play History — the pure half. Classifies backend session rows (hollow rows
   that never had a question answered are hidden, in-progress rows get their own
   style), groups them by local day with "Today / Yesterday / Aug 17 / Jul 13,
   2026" labels, and renders the list as an HTML string. No DOM, no fetch —
   auth.ts:showHistory owns the wiring. */
import { fmtCents, esc } from '../engine';
import { icon } from './icons';

/** Structural twin of api.HistorySession (camelCased SessionHistoryRow). */
export interface HistoryRow {
  sessionId: string;
  mode: string;
  startedUtc: string;
  completedUtc: string | null;
  finalBankCents: number | null;
  accuracyPct: number | null;
  medalTier: string | null;
  xpEarned: number | null;
  /** db/051: 0-based round to resume at, live bank, hard mode. Optional —
      older API builds (usp_Session_History) don't send them. */
  currentRoundIndex?: number;
  bankCents?: number;
  hardMode?: boolean;
}

export type SessionKind = 'completed' | 'inProgress' | 'hollow';

export interface DayGroup<T extends HistoryRow = HistoryRow> {
  /** 'Today' | 'Yesterday' | 'Aug 17' | 'Jul 13, 2026' */
  label: string;
  /** local YYYY-MM-DD — stable across renders, handy for tests */
  key: string;
  rows: T[];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MEDAL_TIERS = new Set(['gold', 'silver', 'bronze', 'wood']);

/** Completed rows carry a CompletedUtc. An incomplete row that earned XP,
    banked money, or (db/051) has moved past round 1 / carries a live bank is a
    game in progress; one with none of those is "hollow" — a row created on a
    Start/Resume tap and abandoned at the intro (84% of rows app-wide as of
    2026-08-21). */
export function classifySession(row: HistoryRow): SessionKind {
  if (row.completedUtc != null) return 'completed';
  if ((row.xpEarned ?? 0) > 0 || (row.finalBankCents ?? 0) > 0) return 'inProgress';
  if ((row.currentRoundIndex ?? 0) > 0 || (row.bankCents ?? 0) > 0) return 'inProgress';
  return 'hollow';
}

/** Rounds per game — mirrors ROUNDS.length in the engine without importing it. */
const ROUND_COUNT = 5;

/** "Round 4 of 5" for an in-progress row, or '' when the API didn't send a
    round index. currentRoundIndex is the 0-based round to RESUME at, so +1;
    clamped so a row parked after the last round never reads "Round 6 of 5". */
export function roundLabel(row: HistoryRow, total: number = ROUND_COUNT): string {
  if (row.currentRoundIndex == null) return '';
  const n = Math.min(Math.max(row.currentRoundIndex, 0) + 1, total);
  return `Round ${n} of ${total}`;
}

/** The bank an in-progress row should show: the live BankCents (db/051) when
    present, else the legacy FinalBankCents; 0 when neither. */
export function liveBank(row: HistoryRow): number {
  return (row.bankCents ?? 0) > 0 ? (row.bankCents as number) : row.finalBankCents ?? 0;
}

/** The moment a row represents: completion if it has one, else its start. */
/**
 * The API serialises DATETIME2 columns without a zone designator
 * ("2026-08-22T01:20:00"), and `new Date()` reads a bare ISO local-time
 * string as LOCAL time — so a 9:20 PM ET game rendered as "Aug 22 · 1:20 AM"
 * (live bug, 2026-08-21). Every *Utc field IS UTC; pin it with a 'Z' unless
 * the string already carries a zone. PURE.
 */
export function parseUtc(s: string): Date {
  const bare = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(s);
  return new Date(bare ? s + 'Z' : s);
}

export function rowDate(row: HistoryRow): Date {
  const d = parseUtc(row.completedUtc ?? row.startedUtc);
  return isNaN(d.getTime()) ? parseUtc(row.startedUtc) : d;
}

function localMidnight(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export function dayKey(d: Date): string {
  if (isNaN(d.getTime())) return 'unknown';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Today / Yesterday in the viewer's local time, then "Aug 17" for the current
    year and "Jul 13, 2026" once the year differs. */
export function dayLabel(date: Date, now: Date = new Date()): string {
  if (isNaN(date.getTime())) return 'Earlier';
  const days = Math.round((localMidnight(now) - localMidnight(date)) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  const md = `${MONTHS[date.getMonth()]} ${date.getDate()}`;
  return date.getFullYear() === now.getFullYear() ? md : `${md}, ${date.getFullYear()}`;
}

/** Newest day first, rows newest-first within a day. Does not filter — callers
    decide which kinds to pass (renderHistory drops hollow rows first). */
export function groupByDay<T extends HistoryRow>(rows: T[], now: Date = new Date()): DayGroup<T>[] {
  const sorted = rows
    .map((r, i) => ({ r, t: rowDate(r).getTime(), i }))
    .sort((a, b) => (isNaN(b.t) ? 0 : b.t) - (isNaN(a.t) ? 0 : a.t) || a.i - b.i);
  const groups: DayGroup<T>[] = [];
  for (const { r } of sorted) {
    const d = rowDate(r);
    const key = dayKey(d);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.rows.push(r);
    else groups.push({ label: dayLabel(d, now), key, rows: [r] });
  }
  return groups;
}

/** "7 sessions · 3 unfinished hidden" — the hidden count keeps the hollow rows
    from feeling lost. */
export function summaryLine(rows: HistoryRow[]): string {
  const n = rows.length;
  const hidden = rows.filter((r) => classifySession(r) === 'hollow').length;
  const base = `${n} session${n === 1 ? '' : 's'}`;
  return hidden > 0 ? `${base} · ${hidden} unfinished hidden` : base;
}

export const EMPTY_HISTORY_HTML =
  `<div class="hist-empty">No finished sessions yet. Your first completed game shows up here.</div>`;

function timeOfDay(d: Date): string {
  if (isNaN(d.getTime())) return '';
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m} ${h < 12 ? 'AM' : 'PM'}`;
}

function xpLabel(xp: number | null): string {
  // Older rows from before db/037 carry no XP at all — a dash, not "0 XP".
  return xp == null ? '—' : `${xp.toLocaleString('en-US')} XP`;
}

function completedRowHTML(row: HistoryRow): string {
  const tier = MEDAL_TIERS.has(row.medalTier ?? '') ? (row.medalTier as string) : '';
  const acc = row.accuracyPct == null ? '' : `<span class="hist-cell">${Math.round(row.accuracyPct)}% right</span>`;
  return (
    `<div class="hist-row hist-row--done" data-session-id="${esc(row.sessionId)}">` +
    `<span class="hist-well hist-medal${tier ? ' ' + tier : ''}" aria-hidden="true">${icon('medal', 24)}</span>` +
    `<div class="hist-main">` +
    `<div class="hist-bank">${fmtCents(row.finalBankCents ?? 0)}</div>` +
    `<div class="hist-strip">` +
    `<span class="hist-cell">${xpLabel(row.xpEarned)}</span>` +
    acc +
    `</div>` +
    `</div>` +
    `<div class="hist-meta hist-time">${timeOfDay(rowDate(row))}</div>` +
    `</div>`
  );
}

/** "In progress · Round 4 of 5 · $4,768 banked · started Aug 17" — every
    piece after "In progress" is optional and joined with middots. */
export function inProgressLine(row: HistoryRow, now: Date = new Date()): string {
  const parts = ['In progress'];
  const round = roundLabel(row);
  if (round) parts.push(round);
  const bank = liveBank(row);
  if (bank > 0) parts.push(`${fmtCents(bank)} banked`);
  const started = parseUtc(row.startedUtc);
  if (!isNaN(started.getTime())) {
    parts.push(`started ${dayLabel(started, now).replace(/^Today$/, 'today').replace(/^Yesterday$/, 'yesterday')}`);
  }
  return parts.join(' · ');
}

function inProgressRowHTML(row: HistoryRow, now: Date): string {
  const line = inProgressLine(row, now);
  const cut = line.indexOf(' · ');
  const title = cut < 0 ? line : line.slice(0, cut);
  const rest = cut < 0 ? '' : line.slice(cut);
  const xp = (row.xpEarned ?? 0) > 0 ? `<span class="hist-cell">${xpLabel(row.xpEarned)}</span>` : '';
  const hard = row.hardMode ? `<span class="hist-cell">${icon('flame', 12)} Hard</span>` : '';
  return (
    `<div class="hist-row hist-row--live" data-session-id="${esc(row.sessionId)}">` +
    `<span class="hist-well hist-live-dot" aria-hidden="true">▶</span>` +
    `<div class="hist-main">` +
    `<div class="hist-live-title">${esc(title)}<span class="hist-meta">${esc(rest)}</span></div>` +
    `<div class="hist-strip">${xp}${hard}</div>` +
    `</div>` +
    `<button class="hist-resume" type="button" data-hist-resume="${esc(row.sessionId)}" aria-label="Resume this game">Resume</button>` +
    `</div>`
  );
}

/** The whole list. Hollow rows are never emitted; everything else sits under a
    sticky day label, in-progress games first within their day. */
export function renderHistory(rows: HistoryRow[], now: Date = new Date()): string {
  const visible = rows.filter((r) => classifySession(r) !== 'hollow');
  if (visible.length === 0) return EMPTY_HISTORY_HTML;
  return groupByDay(visible, now)
    .map((g) => {
      const live = g.rows.filter((r) => classifySession(r) === 'inProgress');
      const done = g.rows.filter((r) => classifySession(r) === 'completed');
      return (
        `<section class="hist-day" data-day="${g.key}">` +
        `<div class="hist-day-label">${esc(g.label)}</div>` +
        live.map((r) => inProgressRowHTML(r, now)).join('') +
        done.map(completedRowHTML).join('') +
        `</section>`
      );
    })
    .join('');
}
